import { useReducer } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/services/api";

function buildMessages(scenario, initialPrompt, turnMessages) {
  return scenario ? [{ role: "ai", text: initialPrompt, id: "intro" }, ...turnMessages] : [];
}

function getCurrentPrompt(messages, initialPrompt) {
  return [...messages].reverse().find((message) => message.role === "ai")?.text || initialPrompt;
}

function appendTurnMessages(items, data, text) {
  return [
    ...items,
    { role: "user", text, id: data.turn.id },
    { role: "ai", text: data.turn.ai_text, id: `${data.turn.id}-ai`, tip: data.turn.feedback_tip, score: data.turn.score },
  ];
}

function useSessionQueries({ initialSessionId, scenario }) {
  const { data: autoSession } = useQuery({
    queryKey: ["auto-session", scenario?.id],
    queryFn: () => api.createSession(scenario.id),
    enabled: Boolean(scenario && !initialSessionId),
    staleTime: Infinity,
  });
  const sessionId = initialSessionId || autoSession?.id;
  const { data: sessionDetails } = useQuery({
    queryKey: ["session", sessionId],
    queryFn: () => api.getSession(sessionId),
    enabled: Boolean(sessionId),
  });
  return { autoSession, sessionDetails, sessionId };
}

const initialLessonState = { draft: "", turnMessages: [], localProgress: null, localFinalFeedback: null };

function lessonReducer(state, action) {
  if (action.type === "draft") return { ...state, draft: action.value };
  if (action.type === "turn-success") {
    return {
      ...state,
      draft: "",
      turnMessages: appendTurnMessages(state.turnMessages, action.data, action.text),
      localProgress: action.data.session.step_index,
      localFinalFeedback: action.data.session.final_feedback || null,
    };
  }
  return state;
}

export function useLessonSession({ scenario, initialSessionId }) {
  const queryClient = useQueryClient();
  const [state, dispatch] = useReducer(lessonReducer, initialLessonState);

  const { autoSession, sessionDetails, sessionId } = useSessionQueries({ initialSessionId, scenario });

  const totalSteps = sessionDetails?.total_steps || scenario?.script?.length || 1;
  const lessonProgress = state.localProgress ?? sessionDetails?.step_index ?? autoSession?.step_index ?? 0;
  const finalFeedback = state.localFinalFeedback || sessionDetails?.final_feedback || autoSession?.final_feedback || null;
  const currentStep = Math.min(lessonProgress + 1, totalSteps);
  const initialPrompt = scenario?.script?.[0]?.ai_prompt || "Let's begin.";
  const messages = buildMessages(scenario, initialPrompt, state.turnMessages);
  const currentPrompt = getCurrentPrompt(messages, initialPrompt);

  const turnMutation = useMutation({
    mutationFn: (text) => api.sendTurn(sessionId, text),
    onSuccess: (data, text) => {
      dispatch({ type: "turn-success", data, text });
      queryClient.setQueryData(["session", sessionId], data.session);
      queryClient.invalidateQueries({ queryKey: ["progress"] });
      queryClient.invalidateQueries({ queryKey: ["sessions"] });
    },
  });

  const submitTurn = () => {
    if (!state.draft.trim() || !sessionId || turnMutation.isPending) return;
    turnMutation.mutate(state.draft.trim());
  };

  return {
    currentPrompt,
    currentStep,
    draft: state.draft,
    finalFeedback,
    lessonProgress,
    messages,
    setDraft: (value) => dispatch({ type: "draft", value }),
    sessionId,
    submitTurn,
    totalSteps,
    turnPending: turnMutation.isPending,
  };
}